import { useCallback, useRef } from 'react';

interface UseInfinityScrollProps {
  hasMore?: boolean;
  isLoading?: boolean;
  onLoadMore?: () => void;
  threshold?: number;
}

/**
 * Hook for implementing infinity scroll with IntersectionObserver
 * @returns callback ref to attach to the sentinel element
 */
export function useInfinityScroll({ hasMore, isLoading, onLoadMore, threshold = 0.1 }: UseInfinityScrollProps) {
  const observerRef = useRef<IntersectionObserver | null>(null);

  const loadMoreRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }

      if (node && hasMore && onLoadMore && !isLoading) {
        observerRef.current = new IntersectionObserver(
          (entries) => {
            if (entries[0].isIntersecting && !isLoading) {
              onLoadMore();
            }
          },
          { threshold }
        );

        observerRef.current.observe(node);
      }
    },
    [hasMore, isLoading, onLoadMore, threshold]
  );

  return loadMoreRef;
}
